import React, { useRef, useState } from "react";
import { notifyAccessRequest, supabase } from "./supabaseClient";

const REASONS = [
  "I'm a potential client",
  "I'm a recruiter",
  "I'm a collaborator or partner",
  "I was referred by someone",
  "Other",
];

const GLOBAL_UNLOCK_KEY = "isUnlocked";
const projectUnlockKey = (projectId) => `portfolio_project_unlocked_${projectId}`;

export const isProjectUnlocked = (projectId) => {
  if (typeof window === "undefined") return false;

  if (localStorage.getItem(GLOBAL_UNLOCK_KEY) === "true") return true;
  if (!projectId) return false;

  return localStorage.getItem(projectUnlockKey(projectId)) === "true";
};

export const saveProjectUnlock = (projectId) => {
  if (typeof window === "undefined") return;

  localStorage.setItem(GLOBAL_UNLOCK_KEY, "true");

  if (projectId) {
    localStorage.setItem(projectUnlockKey(projectId), "true");
  }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const PasswordGate = ({
  projectId,
  projectTitle = "Protected project",
  onUnlock,
  onClose,
}) => {
  const [view, setView] = useState("unlock");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [requestError, setRequestError] = useState("");

  const triggerError = (message = "Incorrect password") => {
    setError(message);
    setShake(false);
    requestAnimationFrame(() => setShake(true));
    inputRef.current?.select();
  };

  const switchView = (nextView) => {
    setView(nextView);
    setError("");
    setRequestError("");
    setEmailError("");
    setSent(false);
  };

  const handleUnlock = async () => {
    if (!password.trim()) {
      triggerError("Please enter a password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data, error: dbError } = await supabase
        .from("access_keys")
        .select("id")
        .eq("password", password.trim())
        .limit(1);

      if (dbError) {
        console.error("Password check failed:", dbError);
        triggerError(dbError.message || "Unable to check password");
        return;
      }

      if (!data || data.length === 0) {
        triggerError("Incorrect password");
        return;
      }

      saveProjectUnlock(projectId);
      onUnlock?.();
    } catch (err) {
      console.error("Password check crashed:", err);
      triggerError("Unable to check password");
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async () => {
    const finalReason = reason === "Other" ? customReason.trim() : reason;
    let hasError = false;

    if (!email.trim()) {
      setEmailError("Email is required");
      hasError = true;
    } else if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      hasError = true;
    } else {
      setEmailError("");
    }

    if (!reason) {
      setRequestError("Please select a reason");
      hasError = true;
    } else if (reason === "Other" && !customReason.trim()) {
      setRequestError("Please describe your reason");
      hasError = true;
    } else {
      setRequestError("");
    }

    if (hasError) return;

    setSending(true);

    const requestMessage = `[${projectTitle || "Protected project"} / ${
      projectId || "unknown"
    }] ${finalReason}`;

    try {
      const { error: insertError } = await supabase
        .from("access_requests")
        .insert({
          email: email.trim(),
          message: requestMessage,
        });

      if (insertError) {
        throw insertError;
      }

      await notifyAccessRequest({
        email: email.trim(),
        message: requestMessage,
        projectId,
        projectTitle,
        reason: finalReason,
      });

      setSent(true);
    } catch (err) {
      console.error("Access request failed:", err);
      setRequestError(err.message || "Failed to send request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div
        style={{ ...styles.box, animation: shake ? "shake 0.4s ease" : "none" }}
        onAnimationEnd={() => setShake(false)}
      >
        {onClose && (
          <button type="button" onClick={onClose} style={styles.closeBtn}>
            x
          </button>
        )}

        {(loading || sending) && (
          <div style={styles.loaderTrack}>
            <div style={styles.loaderBar} />
          </div>
        )}

        <div style={styles.iconWrap}>
          {view === "unlock" ? <LockIcon /> : <KeyIcon />}
        </div>

        <h2 style={styles.title}>
          {view === "unlock" ? projectTitle : "Request access"}
        </h2>

        <p style={styles.subtitle}>
          {view === "unlock"
            ? "Enter password to continue"
            : "We'll review your request and send a password"}
        </p>

        {view === "unlock" && (
          <>
            <div style={styles.inputWrap}>
              <input
                ref={inputRef}
                type={visible ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleUnlock();
                }}
                style={{ ...styles.input, paddingRight: 36 }}
                autoFocus
              />

              <button
                onClick={() => setVisible((value) => !value)}
                style={styles.eyeBtn}
                type="button"
                aria-label={visible ? "Hide password" : "Show password"}
              >
                {visible ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>

            <p style={styles.errorText}>{error || " "}</p>

            <button
              onClick={handleUnlock}
              disabled={loading}
              style={{
                ...styles.primaryBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "wait" : "pointer",
              }}
              type="button"
            >
              {loading ? "Checking..." : "Unlock"}
            </button>

            <button
              onClick={() => switchView("request")}
              style={styles.ghostBtn}
              type="button"
            >
              Request access
            </button>
          </>
        )}

        {view === "request" && (
          <>
            {sent ? (
              <div style={styles.successBox}>
                <CheckIcon />
                <p style={styles.successTitle}>Request sent</p>
                <p style={styles.successText}>We'll be in touch at {email}</p>
              </div>
            ) : (
              <>
                <input
                  type="email"
                  placeholder="Your email *"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError("");
                  }}
                  onBlur={() => {
                    if (!email.trim()) setEmailError("Email is required");
                    else if (!isValidEmail(email)) {
                      setEmailError("Enter a valid email address");
                    } else {
                      setEmailError("");
                    }
                  }}
                  style={{
                    ...styles.input,
                    borderColor: emailError ? "#e53e3e" : "#d0d0d0",
                  }}
                  autoFocus
                />

                <p style={{ ...styles.fieldError, opacity: emailError ? 1 : 0 }}>
                  {emailError || " "}
                </p>

                <select
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setCustomReason("");
                    setRequestError("");
                  }}
                  style={{
                    ...styles.input,
                    color: reason ? "#121212" : "#aaa",
                    appearance: "none",
                    borderColor:
                      requestError && !reason ? "#e53e3e" : "#d0d0d0",
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23aaa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    paddingRight: 32,
                  }}
                >
                  <option value="" disabled hidden>
                    Reason for access *
                  </option>

                  {REASONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                {reason === "Other" && (
                  <textarea
                    placeholder="Describe your reason... *"
                    value={customReason}
                    onChange={(event) => {
                      setCustomReason(event.target.value);
                      setRequestError("");
                    }}
                    rows={3}
                    style={{
                      ...styles.input,
                      marginTop: 8,
                      resize: "vertical",
                      lineHeight: 1.5,
                      borderColor:
                        requestError && !customReason.trim()
                          ? "#e53e3e"
                          : "#d0d0d0",
                    }}
                  />
                )}

                <p style={styles.errorText}>{requestError || " "}</p>

                <button
                  onClick={handleRequest}
                  disabled={sending}
                  style={{
                    ...styles.primaryBtn,
                    opacity: sending ? 0.7 : 1,
                    cursor: sending ? "wait" : "pointer",
                  }}
                  type="button"
                >
                  {sending ? "Sending..." : "Send request"}
                </button>
              </>
            )}

            <button
              onClick={() => switchView("unlock")}
              style={styles.ghostBtn}
              type="button"
            >
              Back to unlock
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }

        @keyframes lineSlide {
          0% { left: -40%; width: 40%; }
          50% { left: 30%; width: 60%; }
          100% { left: 110%; width: 40%; }
        }
      `}</style>
    </div>
  );
};

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const KeyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export default PasswordGate;

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.82)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 18,
  },
  box: {
    position: "relative",
    background: "#fff",
    padding: "2rem 1.75rem",
    borderRadius: 12,
    textAlign: "center",
    width: 310,
    border: "1px solid #e0e0e0",
    overflow: "hidden",
    boxShadow: "0 24px 80px rgba(0,0,0,0.32)",
  },
  closeBtn: {
    position: "absolute",
    top: 10,
    right: 12,
    border: 0,
    background: "transparent",
    color: "#777",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    zIndex: 20,
  },
  loaderTrack: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: "#f0f0f0",
    zIndex: 10,
    overflow: "hidden",
  },
  loaderBar: {
    position: "absolute",
    top: 0,
    height: "100%",
    background: "#121212",
    animation: "lineSlide 1.2s ease-in-out infinite",
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "#f2f2f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.25rem",
  },
  title: {
    margin: "0 0 4px",
    fontSize: 16,
    fontWeight: 500,
    color: "#121212",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  subtitle: {
    margin: "0 0 1.5rem",
    fontSize: 13,
    color: "#888",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  inputWrap: {
    position: "relative",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    fontSize: 14,
    border: "1px solid #d0d0d0",
    borderRadius: 6,
    outline: "none",
    color: "#121212",
    background: "#fff",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
    transition: "border-color 0.15s",
  },
  eyeBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: "#aaa",
    display: "flex",
    alignItems: "center",
  },
  errorText: {
    fontSize: 12,
    color: "#e53e3e",
    minHeight: 16,
    margin: "6px 0 8px",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  fieldError: {
    fontSize: 12,
    color: "#e53e3e",
    minHeight: 14,
    margin: "4px 0 8px",
    textAlign: "left",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
    transition: "opacity 0.15s",
  },
  primaryBtn: {
    width: "100%",
    padding: 10,
    background: "#121212",
    border: "none",
    color: "#fff",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  ghostBtn: {
    marginTop: 10,
    width: "100%",
    padding: 9,
    background: "none",
    border: "1px solid #000000",
    color: "#000000",
    borderRadius: 6,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  successBox: {
    padding: "1.25rem 0 0.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "PP Neue Montreal, Outfit, sans-serif",
  },
  successTitle: {
    margin: "8px 0 0",
    fontSize: 14,
    color: "#121212",
    fontWeight: 500,
  },
  successText: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#888",
  },
};



